import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import fitzaaLogo from '../../assets/fitzaaLogo.gif';

// Instant jump with subtle element animation - NO SCROLL ANIMATION
const smoothScrollTo = (elementId: string, offset: number = 80) => {
  const element = document.getElementById(elementId);
  if (!element) return;

  const elementPosition = element.getBoundingClientRect().top;
  const targetPosition = window.pageYOffset + elementPosition - offset;

  // Instantly jump to the target position - NO ANIMATION
  window.scrollTo({
    top: targetPosition,
    behavior: 'instant' as ScrollBehavior
  });

  // Small delay to ensure scroll is complete before element animation
  setTimeout(() => {
    // Add subtle animation effect to the target element
    element.style.opacity = '0';
    element.style.transform = 'translateY(20px)';
    element.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';

    // Trigger animation
    requestAnimationFrame(() => {
      element.style.opacity = '1';
      element.style.transform = 'translateY(0)';
    });

    // Clean up after animation
    setTimeout(() => {
      element.style.transition = '';
    }, 600);
  }, 10);
};

const MarketingHeader: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isLandingPage = location.pathname === '/' || location.pathname === '/homepage' || location.pathname === '/company';

  const handleNavClick = (elementId: string) => {
    if (!isLandingPage) {
      // Navigate to landing page first, then scroll after page loads
      navigate(`/#${elementId}`);
      setTimeout(() => {
        smoothScrollTo(elementId);
      }, 100);
    } else {
      smoothScrollTo(elementId);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-lg border-b border-gray-200/50 dark:border-slate-700/50 shadow-lg">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link
              to="/"
              className="flex items-center space-x-3 cursor-pointer group"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 blur-md opacity-50 group-hover:opacity-75 transition-opacity"></div>
                <img
                  src={fitzaaLogo}
                  alt="Fitzaa"
                  className="relative h-12 w-12 group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent group-hover:from-purple-700 group-hover:to-pink-700 transition-all">
                  Fitzaa
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 -mt-1">Wellness Platform</span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-8">
            <div className="relative group">
              <button className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 px-3 py-2 text-base font-medium">
                Solutions
              </button>
              <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-slate-800 ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="py-1">
                  <a
                    href="#steps-challenge"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick('steps-challenge');
                    }}
                    className="block px-4 py-2 text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    Steps Challenge
                  </a>
                  <a
                    href="#multi-activity"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick('multi-activity');
                    }}
                    className="block px-4 py-2 text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    Multi-activity Challenges
                  </a>
                  <a
                    href="#team-challenge"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick('team-challenge');
                    }}
                    className="block px-4 py-2 text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    Team Challenge
                  </a>
                  <a
                    href="#virtual-marathon"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick('virtual-marathon');
                    }}
                    className="block px-4 py-2 text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    One Day Virtual Marathon
                  </a>
                </div>
              </div>
            </div>
            <div className="relative group">
              <button className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 px-3 py-2 text-base font-medium">
                Features
              </button>
              <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-slate-800 ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="py-1">
                  <a
                    href="#activity-tracking"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick('activity-tracking');
                    }}
                    className="block px-4 py-2 text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    Activity Tracking
                  </a>
                  <a
                    href="#nutrition"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick('nutrition');
                    }}
                    className="block px-4 py-2 text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    Nutrition & Hydration
                  </a>
                  <a
                    href="#fitness"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick('fitness');
                    }}
                    className="block px-4 py-2 text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    Fitness & Exercise
                  </a>
                  <a
                    href="#health-metrics"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick('health-metrics');
                    }}
                    className="block px-4 py-2 text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    Health Metrics
                  </a>
                  <a
                    href="#mental-wellbeing"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick('mental-wellbeing');
                    }}
                    className="block px-4 py-2 text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    Mental Well-being
                  </a>
                </div>
              </div>
            </div>
            <a
              href="#pricing"
              onClick={(e) => {
                e.preventDefault();
                handleNavClick('pricing');
              }}
              className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 px-3 py-2 text-base font-medium cursor-pointer"
            >
              Pricing
            </a>
            <div className="relative group">
              <button className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 px-3 py-2 text-base font-medium">
                Resources
              </button>
              <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-slate-800 ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="py-1">
                  <a
                    href="#blog"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick('blog');
                    }}
                    className="block px-4 py-2 text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    Blog
                  </a>
                  <a
                    href="#success-stories"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick('success-stories');
                    }}
                    className="block px-4 py-2 text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    Client Success Stories
                  </a>
                  <a
                    href="#tools"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick('tools');
                    }}
                    className="block px-4 py-2 text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    Tools and Templates
                  </a>
                  <a
                    href="#guides"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick('guides');
                    }}
                    className="block px-4 py-2 text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    Guides
                  </a>
                  <a
                    href="#help"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick('help');
                    }}
                    className="block px-4 py-2 text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    Help Center
                  </a>
                </div>
              </div>
            </div>
            <Link
              to="/login"
              className="ml-4 px-4 py-2 text-base font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
            >
              Login
            </Link>
            <Link
              to="/contact-sales"
              className="ml-4 px-4 py-2 text-base font-medium text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400"
            >
              Contact Sales
            </Link>
            <Link
              to="/contact-sales?source=book-demo"
              className="ml-4 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-base font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 cursor-pointer"
            >
              Book a Demo
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-2">
            <a
              href="#solutions"
              onClick={(e) => {
                e.preventDefault();
                setMobileMenuOpen(false);
                handleNavClick('solutions');
              }}
              className="block px-3 py-2 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md"
            >
              Solutions
            </a>
            <a
              href="#features"
              onClick={(e) => {
                e.preventDefault();
                setMobileMenuOpen(false);
                handleNavClick('features');
              }}
              className="block px-3 py-2 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md"
            >
              Features
            </a>
            <a
              href="#pricing"
              onClick={(e) => {
                e.preventDefault();
                setMobileMenuOpen(false);
                handleNavClick('pricing');
              }}
              className="block px-3 py-2 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md"
            >
              Pricing
            </a>
            <a
              href="#resources"
              onClick={(e) => {
                e.preventDefault();
                setMobileMenuOpen(false);
                handleNavClick('resources');
              }}
              className="block px-3 py-2 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md"
            >
              Resources
            </a>
            <Link
              to="/login"
              className="block px-3 py-2 text-base font-medium text-purple-600 dark:text-purple-400"
              onClick={() => setMobileMenuOpen(false)}
            >
              Login
            </Link>
            <Link
              to="/contact-sales?source=mobile-menu"
              className="block px-3 py-2 text-base font-medium bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-md text-center"
              onClick={() => setMobileMenuOpen(false)}
            >
              Book a Demo
            </Link>
          </div>
        )}
      </nav>
    </header>
  );
};

export default MarketingHeader;

